"use client"

import { useState, useEffect } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Users, UserPlus, Mail, AlertCircle, Search, ChevronDown, Building2, MoreVertical, Trash2 } from "lucide-react"
// import Image from "@/src/components/Image"
import { BranchAssignmentDialog } from "@/components/team/BranchAssignmentDialog"
import { 
  getTeamMembers,
  getBranches,
  cancelInvitation,
  removeMember,
  getRoleDisplayName,
  getRoleBadgeColor,
  hasPermission 
} from "@/lib/api/team"
import type { TeamMember, TeamInvitation, Branch } from "@/src/types/team"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useAuth } from "@/src/contexts/AuthContext"
import { useRouter } from "@/src/hooks/useRouter"
import { toast } from "sonner"

export default function TeamManagementPage() {
  const { user } = useAuth()
  const router = useRouter()
  const [members, setMembers] = useState<TeamMember[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<TeamInvitation[]>([])
  const [branches, setBranches] = useState<Branch[]>([])
  const [selectedBranchId, setSelectedBranchId] = useState<string | undefined>(undefined)
  const [searchQuery, setSearchQuery] = useState("")
  const [activeTab, setActiveTab] = useState<"members" | "invitations">("members")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const [deleteMemberState, setDeleteMemberState] = useState<{
    isOpen: boolean
    memberId: string | null
    memberName: string | null
  }>({ isOpen: false, memberId: null, memberName: null })
  const [deletingMember, setDeletingMember] = useState(false)
  
  // Branch assignment dialog state
  const [branchDialogState, setBranchDialogState] = useState<{
    isOpen: boolean;
    userId: string | null;
    userName: string | null;
    userType: 'owner' | 'admin' | 'staff';
  }>({
    isOpen: false,
    userId: null,
    userName: null,
    userType: 'staff',
  })

  const userPermissions = user?.permissions || []

  const canInvite = hasPermission(userPermissions, 'team:invite')
  const canManage = hasPermission(userPermissions, 'team:manage')
  const canView = hasPermission(userPermissions, 'team:view')

  const loadTeamData = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getTeamMembers(selectedBranchId)
      
      if (response.success && response.data) {
        setMembers(response.data.members || [])
        setPendingInvitations(response.data.invitations || [])
      }
    } catch (err: any) {
      console.error('Failed to load team data:', err)
      setError(err.response?.data?.error || 'ไม่สามารถโหลดข้อมูลทีมได้')
      toast.error('เกิดข้อผิดพลาด', {
        description: 'ไม่สามารถโหลดข้อมูลทีมได้'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadBranches = async () => {
    try {
      const response = await getBranches()
      if (response.success && response.data) {
        setBranches(response.data as Branch[])
      }
    } catch (err) {
      console.error('Failed to load branches:', err)
      // Don't show error toast - branches are optional
    }
  }

  useEffect(() => {
    if (canView) {
      loadBranches()
      loadTeamData()
    }
  }, [canView, selectedBranchId])

  const handleSearch = () => {
    // Filter members based on search query
    loadTeamData()
  }

  const handleAssignBranches = (userId: string, userName: string, userType: 'owner' | 'admin' | 'staff') => {
    // ปิด dropdown ก่อน
    setOpenDropdownId(null)
    // รอให้ dropdown ปิดก่อนเปิด modal
    setTimeout(() => {
      setBranchDialogState({
        isOpen: true,
        userId,
        userName,
        userType,
      })
    }, 100)
  }

  const handleDeleteMember = async () => {
    if (!deleteMemberState.memberId) return
    try {
      setDeletingMember(true)
      await removeMember(deleteMemberState.memberId)
      toast.success(`ลบ ${deleteMemberState.memberName} ออกจากทีมแล้ว`)
      setDeleteMemberState({ isOpen: false, memberId: null, memberName: null })
      loadTeamData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'ไม่สามารถลบสมาชิกได้')
    } finally {
      setDeletingMember(false)
    }
  }

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    try {
      setCancellingId(invitationId)
      await cancelInvitation(invitationId)
      toast.success(`ยกเลิกคำเชิญไปยัง ${email} แล้ว`)
      loadTeamData()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'ไม่สามารถยกเลิกคำเชิญได้')
    } finally {
      setCancellingId(null)
    }
  }

  const handleBranchAssignmentSuccess = () => {
    loadTeamData()
    setBranchDialogState({
      isOpen: false,
      userId: null,
      userName: null,
      userType: 'staff',
    })
  }

  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return (
      member.email?.toLowerCase().includes(query) ||
      member.full_name?.toLowerCase().includes(query) ||
      member.phone?.toLowerCase().includes(query)
    )
  })

  const filteredInvitations = pendingInvitations.filter(invitation => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    return invitation.email?.toLowerCase().includes(query)
  })

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">จัดการทีมงาน</h1>
          
          {canInvite && (
            <Button 
              className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
              onClick={() => router.push('/dashboard/team/invite')}
            >
              <UserPlus className="h-4 w-4" />
              เชิญสมาชิกใหม่
            </Button>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <TabGroup value={activeTab} onValueChange={(v) => setActiveTab(v as "members" | "invitations")} color="brand-blue" fullWidth>
          <TabGroupList>
            <TabGroupTrigger value="members">
              <Users className="h-4 w-4 mr-2" />
              สมาชิกในทีม ({filteredMembers.length})
            </TabGroupTrigger>
            <TabGroupTrigger value="invitations">
              <Mail className="h-4 w-4 mr-2" />
              ประวัติการเชิญ ({filteredInvitations.length})
            </TabGroupTrigger>
          </TabGroupList>
        </TabGroup>

        {/* Search and Filter Section */}
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
          {/* Search Bar */}
          <div className="flex gap-2 flex-1 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input 
                type="text" 
                placeholder="ชื่อสมาชิก, เบอร์โทร, อีเมล" 
                className="pl-10 h-11"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button 
              variant="secondary" 
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 h-11"
              onClick={handleSearch}
            >
              ค้นหา
            </Button>
          </div>

          {/* Branch Filter */}
          <div className="flex items-center gap-3">
            <span className="text-gray-700 font-medium">สาขา</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2 h-11 px-4 bg-transparent min-w-[140px]">
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  <span>{selectedBranchId ? (branches || []).find(b => b.id === selectedBranchId)?.branch_name : 'ทั้งหมด'}</span>
                  <ChevronDown className="h-4 w-4 ml-auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-white">
                <DropdownMenuItem onClick={() => setSelectedBranchId(undefined)}>
                  ทั้งหมด
                </DropdownMenuItem>
                {(branches || []).map((branch) => (
                  <DropdownMenuItem key={branch.id} onClick={() => setSelectedBranchId(branch.id)}>
                    {branch.branch_name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {/* Members Table */}
            {activeTab === "members" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-normal text-gray-900">
                    รายชื่อ <span className="text-purple-600">สมาชิกในทีม ({filteredMembers.length})</span>
                  </h2>
                </div>

                {filteredMembers.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">ไม่พบสมาชิกในทีม</p>
                    {canInvite && (
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={() => router.push('/dashboard/team/invite')}
                      >
                        เชิญสมาชิกคนแรก
                      </Button>
                    )}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-gray-700">เบอร์/ชื่อ-สกุล/อีเมล</TableHead>
                        <TableHead className="text-gray-700">บทบาท</TableHead>
                        <TableHead className="text-gray-700">สาขาที่กำหนด</TableHead>
                        <TableHead className="text-gray-700">วันที่เข้าร่วม</TableHead>
                        <TableHead className="text-gray-700">สถานะ</TableHead>
                        {canManage && <TableHead className="text-gray-700 text-right">จัดการ</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMembers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{member.phone || '-'}</span>
                              <span className="text-sm text-gray-600">{member.full_name || 'ไม่ระบุชื่อ'}</span>
                              <span className="text-sm text-gray-500">{member.email}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(member.user_type)}`}>
                              {getRoleDisplayName(member.user_type)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {member.user_type === 'owner' ? (
                              <span className="text-sm text-gray-500 italic">ทุกสาขา</span>
                            ) : member.assigned_branches && member.assigned_branches.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {member.assigned_branches.map((branch) => (
                                  <div key={branch.id} className="flex items-center gap-2">
                                    <Building2 className="h-3 w-3 text-purple-600" />
                                    <span className="text-sm text-gray-700">
                                      {branch.branch_name}
                                      {branch.branch_code && (
                                        <span className="text-gray-500 ml-1">({branch.branch_code})</span>
                                      )}
                                    </span>
                                  </div>
                                ))}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-purple-600 hover:text-purple-700 w-fit mt-1"
                                  onClick={() => handleAssignBranches(member.id, member.full_name || member.email, member.user_type)}
                                >
                                  <Building2 className="h-4 w-4 mr-1" />
                                  แก้ไข
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600 hover:text-purple-700"
                                onClick={() => handleAssignBranches(member.id, member.full_name || member.email, member.user_type)}
                              >
                                <Building2 className="h-4 w-4 mr-1" />
                                กำหนดสาขา
                              </Button>
                            )}
                          </TableCell>
                          <TableCell>
                            {member.invitation_accepted_at 
                              ? new Date(member.invitation_accepted_at).toLocaleDateString('th-TH')
                              : new Date(member.created_at).toLocaleDateString('th-TH')
                            }
                          </TableCell>
                          <TableCell>
                            <span className={member.is_active ? "text-green-600 font-medium" : "text-gray-400 font-medium"}>
                              {member.is_active ? 'ใช้งาน' : 'ระงับ'}
                            </span>
                          </TableCell>
                          {canManage && (
                            <TableCell className="text-right">
                              {member.user_type !== 'owner' ? (
                                <DropdownMenu 
                                  open={openDropdownId === member.id}
                                  onOpenChange={(open) => setOpenDropdownId(open ? member.id : null)}
                                >
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-white">
                                    <DropdownMenuItem onClick={() => handleAssignBranches(member.id, member.full_name || member.email, member.user_type)}>
                                      <Building2 className="h-4 w-4 mr-2" />
                                      กำหนดสาขา
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                      className="text-red-600 focus:text-red-600"
                                      onClick={() => {
                                        setOpenDropdownId(null)
                                        setTimeout(() => {
                                          setDeleteMemberState({ isOpen: true, memberId: member.id, memberName: member.full_name || member.email })
                                        }, 100)
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      ลบสมาชิก
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}

            {/* Invitations Table */}
            {activeTab === "invitations" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-normal text-gray-900">
                    รายชื่อ <span className="text-orange-600">คำเชิญที่รอการยอมรับ ({filteredInvitations.length})</span>
                  </h2>
                </div>

                {filteredInvitations.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">ไม่มีคำเชิญที่รอการยอมรับ</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-gray-700">อีเมล</TableHead>
                        <TableHead className="text-gray-700">บทบาท</TableHead>
                        <TableHead className="text-gray-700">สาขาที่กำหนด</TableHead>
                        <TableHead className="text-gray-700">วันที่เชิญ</TableHead>
                        <TableHead className="text-gray-700">หมดอายุ</TableHead>
                        <TableHead className="text-gray-700">สถานะ</TableHead>
                        {canInvite && <TableHead className="text-gray-700 text-right">จัดการ</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInvitations.map((invitation) => (
                        <TableRow key={invitation.id}>
                          <TableCell className="font-medium">{invitation.email}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(invitation.role_code as any)}`}>
                              {getRoleDisplayName(invitation.role_code as any)}
                            </span>
                          </TableCell>
                          <TableCell>
                            {invitation.assigned_branch_ids && invitation.assigned_branch_ids.length > 0
                              ? `${invitation.assigned_branch_ids.length} สาขา`
                              : <span className="text-gray-400">ยังไม่กำหนด</span>
                            }
                          </TableCell>
                          <TableCell>{new Date(invitation.created_at).toLocaleDateString('th-TH')}</TableCell>
                          <TableCell>{new Date(invitation.expires_at).toLocaleDateString('th-TH')}</TableCell>
                          <TableCell>
                            <span className="text-orange-500 font-medium">{invitation.status === 'pending' ? 'รอตอบรับ' : invitation.status}</span>
                          </TableCell>
                          {canInvite && (
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                disabled={cancellingId === invitation.id}
                                onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                              >
                                {cancellingId === invitation.id ? 'กำลังยกเลิก...' : 'ยกเลิก'}
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}

            {/* Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="text-sm text-purple-700 mb-1">เจ้าของ</div>
                <div className="text-2xl font-bold text-purple-900">
                  {members.filter(m => m.user_type === 'owner').length}
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="text-sm text-blue-700 mb-1">ผู้จัดการ</div>
                <div className="text-2xl font-bold text-blue-900">
                  {members.filter(m => m.user_type === 'admin').length}
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="text-sm text-green-700 mb-1">พนักงาน</div>
                <div className="text-2xl font-bold text-green-900">
                  {members.filter(m => m.user_type === 'staff').length}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Member Confirmation Dialog */}
      <AlertDialog open={deleteMemberState.isOpen} onOpenChange={(open) => !deletingMember && setDeleteMemberState(prev => ({ ...prev, isOpen: open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการลบสมาชิก</AlertDialogTitle>
            <AlertDialogDescription>
              คุณแน่ใจหรือไม่ว่าต้องการลบ <strong>{deleteMemberState.memberName}</strong> ออกจากทีม?
              <br />
              <br />
              สมาชิกคนนี้จะไม่สามารถเข้าใช้งานระบบได้อีกต่อไป
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingMember}>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMember}
              disabled={deletingMember}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingMember ? 'กำลังลบ...' : 'ลบสมาชิก'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Branch Assignment Dialog */}
      <BranchAssignmentDialog
        isOpen={branchDialogState.isOpen}
        onClose={() => setBranchDialogState({ isOpen: false, userId: null, userName: null, userType: 'staff' })}
        userId={branchDialogState.userId}
        userName={branchDialogState.userName}
        userType={branchDialogState.userType}
        availableBranches={branches || []}
        onSuccess={handleBranchAssignmentSuccess}
      />
    </DashboardLayout>
  )
}
